<template>
  <div>
    <div class="settings-card-top"></div>

    <div class="settings-card-body">
      <br />
      <h4>Información de cuenta</h4>
      <p class="settings-subtitle">Gestiona los detalles generales de tu empresa o persona.</p>
      <div v-if="sectionError" class="alert alert-danger" role="alert">{{ sectionError }}</div>

      <div class="settings-grid company-grid">
        <div class="logo-upload-box">
          <label>Logo de la empresa</label>
          <label v-if="!logoPreviewUrl" for="logoUploadInput" class="logo-upload-btn">
            <i class="fa fa-cloud-upload"></i>
            <span>SUBIR LOGO</span>
          </label>
          <div v-else class="logo-preview-box">
            <img :src="logoPreviewUrl" alt="Logo de la empresa" />
          </div>
          <div v-if="logoPreviewUrl" class="logo-actions-row">
            <label for="logoUploadInput" class="logo-change-btn">
              CAMBIAR LOGO
            </label>
            <button type="button" class="logo-delete-btn" @click="openDeleteLogoConfirm" title="Eliminar logo">
              <i class="fa fa-trash-o"></i>
            </button>
          </div>
          <input
            id="logoUploadInput"
            ref="logoUploadInput"
            class="logo-file-input"
            type="file"
            accept=".jpg,.jpeg,.png,image/jpeg,image/png"
            @change="handleLogoFileChange"
          />
          <small>JPG o PNG. Máx. 2MB.</small>
          <small v-if="logoError" class="text-danger d-block mt-2">{{ logoError }}</small>
        </div>

        <div class="settings-field settings-field--trade-name">
          <label>Nombre Comercial</label>
          <input
            type="text"
            class="form-control"
            v-model="account.tradeName"
            placeholder="Ej. ServDigital MX"
            @input="normalizeTradeName"
          />
        </div>

        <div class="settings-field settings-field--main-rfc">
          <label>RFC Principal</label>
          <multiselect
            v-model="selectedRfcOption"
            :options="rfcSelectOptions"
            track-by="value"
            label="label"
            :show-labels="false"
            :searchable="true"
            :allow-empty="true"
            placeholder="Sin RFC asignado"
            class="settings-rfc-select"
          >
            <template #noResult>
              <span>Sin coincidencias</span>
            </template>
            <template #noOptions>
              <span>Sin RFC registrados</span>
            </template>
          </multiselect>
        </div>
        <div class="settings-field settings-field--primary-email">
          <label>Correo principal</label>
          <input
            type="email"
            class="form-control"
            :value="account.email || ''"
            placeholder="Sin correo principal"
            disabled
          />
        </div>
        <div class="settings-field settings-field--alternate-email">
          <label>Correo electrónico alternativo</label>
          <input
            type="email"
            class="form-control"
            v-model="account.email_alt"
            placeholder="correo@empresa.com"
            @input="normalizeAlternateEmail"
          />
        </div>

        <div class="settings-field settings-field--phone">
          <label>Teléfono</label>
          <input type="text" class="form-control" v-model="account.phone" placeholder="55 1234 5678" />
        </div>

        <div class="settings-field settings-field--whatsapp">
          <label>WhatsApp</label>
          <div class="whatsapp-input-wrap" :class="whatsappStatusClass">
            <input
              type="text"
              class="form-control whatsapp-input"
              :class="whatsappStatusClass"
              v-model="account.whatsapp_phone"
              placeholder="10 dígitos, sin espacios ni guiones (Ej. 5512345678)"
            />
            <span v-if="whatsappStatusIcon" class="whatsapp-input-icon" :class="whatsappStatusClass" aria-hidden="true">
              <i :class="whatsappStatusIcon"></i>
            </span>
          </div>
        </div>
      </div>

      <div class="settings-actions">
        <div v-if="formError" class="alert alert-danger w-100 mb-3" role="alert">{{ formError }}</div>
        <button type="button" class="btn btn-light" @click="resetAccount">Descartar cambios</button>
        <button type="button" class="btn btn-primary" @click="saveAccount">Guardar cambios</button>
      </div>
    </div>

    <ConfirmActionModal
      :show="showDeleteLogoConfirm"
      title="Eliminar logotipo"
      message="Estás por eliminar el logotipo de la empresa. Esta acción puede afectar la identidad visual en tus documentos. ¿Deseas continuar?"
      confirmText="Eliminar"
      cancelText="Cancelar"
      @cancel="closeDeleteLogoConfirm"
      @confirm="confirmDeleteLogo"
    />

    <div v-if="showWhatsappVerificationModal" class="whatsapp-code-modal-backdrop" @click.self="closeWhatsappVerificationModal">
      <div class="whatsapp-code-modal">
        <h5>Confirmar número de WhatsApp</h5>
        <p>
          Te enviamos un código de 6 dígitos a
          <strong>{{ account.whatsapp_phone }}</strong>.
        </p>

        <div class="whatsapp-code-inputs" @paste="handleWhatsappCodePaste">
          <input
            v-for="(digit, index) in whatsappVerificationDigits"
            :key="index"
            :ref="`whatsappCodeDigit${index}`"
            type="text"
            inputmode="numeric"
            pattern="[0-9]*"
            maxlength="1"
            class="whatsapp-code-digit"
            :value="digit"
            @input="onWhatsappCodeInput(index, $event)"
            @keydown="onWhatsappCodeKeydown(index, $event)"
          />
        </div>

        <small v-if="whatsappVerificationError" class="text-danger d-block mt-2">
          {{ whatsappVerificationError }}
        </small>

        <div class="whatsapp-resend-row mt-2">
          <button
            type="button"
            class="btn btn-link p-0 whatsapp-resend-link"
            :disabled="!canResendWhatsappCode || isConfirmingWhatsappCode || isResendingWhatsappCode"
            @click="resendWhatsappVerificationCode"
          >
            {{ isResendingWhatsappCode ? 'Reenviando...' : 'Volver a enviar codigo' }}
          </button>
          <small v-if="!canResendWhatsappCode" class="text-muted">
            Disponible en {{ formattedWhatsappResendCountdown }}
          </small>
        </div>

        <div class="whatsapp-help-box mt-3" v-if="whatsappSupportWaLink">
          <p class="mb-2">
            Si no recibes el mensaje, puedes escribir directamente al WhatsApp de TusCFDIs y solicitar tu codigo.
          </p>
          <div class="whatsapp-help-content">
            <a :href="whatsappSupportWaLink" target="_blank" rel="noopener noreferrer" class="whatsapp-help-link">
              Abrir chat en WhatsApp
            </a>
            <img :src="whatsappSupportQrUrl" alt="QR para abrir WhatsApp de TusCFDIs" class="whatsapp-help-qr" />
          </div>
        </div>

        <div class="whatsapp-code-actions mt-3">
          <button type="button" class="btn btn-light" @click="closeWhatsappVerificationModal" :disabled="isConfirmingWhatsappCode">
            Cancelar
          </button>
          <span v-if="isConfirmingWhatsappCode" class="text-muted whatsapp-code-validating">Validando código...</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ws from '@/services/rfcs';
import accountService from '@/services/account';
import { normalizeEmail, normalizeName } from '@/mixins/normalizers';
import ConfirmActionModal from '@/components/global/ConfirmActionModal.vue';

export default {
  name: 'SettingsAccountSection',
  components: {
    ConfirmActionModal,
  },
  data() {
    return {
      accountDefault: {
        businessName: '',
        tradeName: '',
        email: '',
        mainRfcId: null,
        email_alt: '',
        phone: '',
        whatsapp_phone: '',
        whatsapp_status: 'not_configured',
      },
      account: {},
      persistedAccount: {
        businessName: '',
        tradeName: '',
        email: '',
        mainRfcId: null,
        email_alt: '',
        phone: '',
        whatsapp_phone: '',
        whatsapp_status: 'not_configured',
      },
      logoFile: null,
      logoPreviewUrl: '',
      persistedLogoUrl: '',
      logoPreviewObjectUrl: null,
      showDeleteLogoConfirm: false,
      rfcOptions: [],
      sectionError: '',
      formError: '',
      logoError: '',
      showWhatsappVerificationModal: false,
      whatsappVerificationDigits: ['', '', '', '', '', ''],
      whatsappVerificationError: '',
      isConfirmingWhatsappCode: false,
      isResendingWhatsappCode: false,
      whatsappResendRemainingSeconds: 0,
      whatsappResendIntervalId: null,
      whatsappSupportWaLink: '',
    };
  },
  computed: {
    rfcSelectOptions() {
      return this.rfcOptions.map((item) => ({
        value: item.id,
        label: item.name ? `${item.rfc} — ${item.name}` : item.rfc,
      }));
    },
    selectedRfcOption: {
      get() {
        return this.rfcSelectOptions.find((item) => item.value === this.account.mainRfcId) || null;
      },
      set(option) {
        this.account.mainRfcId = option?.value || null;
      },
    },
    whatsappStatusClass() {
      const status = this.account?.whatsapp_status || 'not_configured';
      if (status === 'active') return 'is-active';
      if (status === 'pending') return 'is-pending';
      return 'is-default';
    },
    whatsappStatusIcon() {
      const status = this.account?.whatsapp_status || 'not_configured';
      if (status === 'active') return 'fa fa-check';
      if (status === 'pending') return 'fa fa-clock-o';
      return '';
    },
    isWhatsappCodeComplete() {
      return this.whatsappVerificationDigits.join('').length === 6;
    },
    canResendWhatsappCode() {
      return this.whatsappResendRemainingSeconds <= 0;
    },
    formattedWhatsappResendCountdown() {
      const value = Math.max(0, Number(this.whatsappResendRemainingSeconds) || 0);
      const mm = String(Math.floor(value / 60)).padStart(2, '0');
      const ss = String(value % 60).padStart(2, '0');
      return `${mm}:${ss}`;
    },
    whatsappSupportQrUrl() {
      if (!this.whatsappSupportWaLink) {
        return '';
      }

      return `https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=${encodeURIComponent(this.whatsappSupportWaLink)}`;
    },
  },
  created() {
    this.resetAccount();
  },
  mounted: async function() {
    await Promise.all([
      this.loadRFCs(),
      this.loadAccountDetails(),
    ]);

    this.ensureMainRfcSelection();
  },
  beforeUnmount() {
    this.clearTemporaryLogoPreview();
    this.stopWhatsappResendCountdown();
  },
  methods: {
    normalizeTradeName() {
      this.account.tradeName = normalizeName(this.account.tradeName || '');
    },
    normalizeAlternateEmail() {
      this.account.email_alt = normalizeEmail(this.account.email_alt || '');
    },
    async loadRFCs() {
      try {
        const response = await ws.getRFCs();

        if (response && response.type === 'success') {
          this.rfcOptions = Array.isArray(response.data) ? response.data : [];
        }
      } catch (error) {
        this.rfcOptions = [];
      }
    },
    async loadAccountDetails() {
      this.sectionError = '';
      try {
        const response = await accountService.getDetails();

        if (response && response.type === 'success') {
          const detailAccount = response.data?.account || {};

          this.account = {
            ...this.account,
            tradeName: detailAccount.name || '',
            email: detailAccount.email || '',
            email_alt: detailAccount.email_alt || '',
            phone: detailAccount.phone || '',
            mainRfcId: response.data?.main_rfc_id || null,
            whatsapp_phone: response.data?.whatsapp?.phone || detailAccount.phone || '',
            whatsapp_status: response.data?.whatsapp?.status || 'not_configured',
          };

          this.persistedAccount = { ...this.account };
          this.persistedLogoUrl = response.data?.logo_url || '';
          this.clearTemporaryLogoPreview();
          this.logoPreviewUrl = this.persistedLogoUrl;
          this.logoFile = null;
        }
      } catch (error) {
        this.sectionError = 'No se pudieron cargar los datos de la cuenta.';
      }
    },
    ensureMainRfcSelection() {
      if (this.account.mainRfcId) return;

      const defaultRfc = this.rfcOptions.find((item) => {
        return item?.default === true || item?.default === 1 || item?.default === '1';
      });

      this.account.mainRfcId = defaultRfc
        ? defaultRfc.id
        : (this.rfcOptions[0]?.id || null);

      this.persistedAccount = { ...this.account };
    },
    async refreshData() {
      await Promise.all([
        this.loadRFCs(),
        this.loadAccountDetails(),
      ]);
      this.ensureMainRfcSelection();
    },
    resetLogoInput() {
      if (this.$refs.logoUploadInput) {
        this.$refs.logoUploadInput.value = '';
      }
    },
    openDeleteLogoConfirm() {
      this.showDeleteLogoConfirm = true;
    },
    closeDeleteLogoConfirm() {
      this.showDeleteLogoConfirm = false;
    },
    clearTemporaryLogoPreview() {
      if (this.logoPreviewObjectUrl) {
        URL.revokeObjectURL(this.logoPreviewObjectUrl);
        this.logoPreviewObjectUrl = null;
      }
    },
    setTemporaryLogoPreview(file) {
      this.clearTemporaryLogoPreview();
      this.logoPreviewObjectUrl = URL.createObjectURL(file);
      this.logoPreviewUrl = this.logoPreviewObjectUrl;
    },
    restorePersistedLogoPreview() {
      this.clearTemporaryLogoPreview();
      this.logoPreviewUrl = this.persistedLogoUrl || '';
    },
    async confirmDeleteLogo() {
      this.showDeleteLogoConfirm = false;
      this.formError = '';

      try {
        const payload = new FormData();
        payload.append('trade_name', this.account.tradeName || '');
        payload.append('email_alt', normalizeEmail(this.account.email_alt || ''));
        payload.append('phone', this.account.phone || '');
        payload.append('main_rfc_id', this.account.mainRfcId || '');
        payload.append('remove_logo', '1');

        const response = await accountService.update(payload);

        if (response && response.type === 'success') {
          this.$toast.success('Logotipo eliminado correctamente.');
          await this.refreshData();
          this.resetLogoInput();
          return;
        }

        this.formError = response?.message || 'No se pudo eliminar el logotipo.';
      } catch (error) {
        this.formError = error?.response?.data?.message || 'No se pudo eliminar el logotipo.';
      }
    },
    resetAccount() {
      this.formError = '';
      this.logoError = '';
      this.account = { ...this.persistedAccount };
      this.logoFile = null;
      this.restorePersistedLogoPreview();
      this.resetLogoInput();
    },
    handleLogoFileChange(event) {
      this.logoError = '';
      this.formError = '';
      const file = event?.target?.files?.[0];

      if (!file) {
        return;
      }

      const allowedTypes = ['image/jpeg', 'image/png'];
      const isAllowedType = allowedTypes.includes(file.type);
      const isAllowedExtension = /\.(jpe?g|png)$/i.test(file.name);

      if (!isAllowedType || !isAllowedExtension) {
        this.logoError = 'Formato no permitido. Solo se aceptan archivos JPG o PNG.';
        event.target.value = '';
        this.logoFile = null;
        return;
      }

      const maxSize = 2 * 1024 * 1024;
      if (file.size > maxSize) {
        this.logoError = 'El archivo no debe exceder 2MB.';
        event.target.value = '';
        this.logoFile = null;
        return;
      }

      this.logoFile = file;
      this.setTemporaryLogoPreview(file);
    },
    async saveAccount() {
      this.formError = '';
      this.normalizeTradeName();
      this.normalizeAlternateEmail();

      const whatsappChanged = this.hasWhatsappChanged();

      const payload = new FormData();
      payload.append('trade_name', this.account.tradeName || '');
      payload.append('email_alt', normalizeEmail(this.account.email_alt || ''));
      payload.append('phone', this.account.phone || '');
      payload.append('main_rfc_id', this.account.mainRfcId || '');

      if (this.logoFile) {
        payload.append('logo', this.logoFile);
      }

      try {
        const response = await accountService.update(payload);

        if (response && response.type === 'success') {
          if (!whatsappChanged) {
            this.$toast.success(response.message || 'Cambios guardados correctamente.');
            await this.refreshData();
            this.resetLogoInput();
            return;
          }

          await this.requestWhatsappVerificationCode();
          this.resetLogoInput();
          return;
        }

        this.formError = response?.message || 'No se pudieron guardar los cambios.';
      } catch (error) {
        this.formError = error?.response?.data?.message || 'No se pudieron guardar los cambios.';
      }
    },
    normalizePhoneComparable(value) {
      const raw = (value || '').toString().trim();
      if (!raw) return '';

      let digits = raw.replace(/[^0-9]/g, '');
      if (!digits) return '';

      if (digits.startsWith('521') && digits.length === 13) {
        return `+${digits}`;
      }

      if (digits.startsWith('52') && digits.length === 12) {
        return `+521${digits.slice(2)}`;
      }

      if (digits.length === 10) {
        return `+521${digits}`;
      }

      if (raw.startsWith('+')) {
        return `+${digits}`;
      }

      return `+${digits}`;
    },
    hasWhatsappChanged() {
      const current = this.normalizePhoneComparable(this.account.whatsapp_phone);
      const previous = this.normalizePhoneComparable(this.persistedAccount.whatsapp_phone);
      return current !== previous;
    },
    async requestWhatsappVerificationCode() {
      this.whatsappVerificationError = '';

      const phone = (this.account.whatsapp_phone || '').toString().trim();
      if (!phone) {
        this.formError = 'Debes capturar un número de WhatsApp para confirmar el cambio.';
        return;
      }

      try {
        const response = await accountService.requestWhatsappVerification({ whatsapp_phone: phone });

        if (response && response.type === 'success') {
          this.$toast.success(response.message || 'Se envió el código de confirmación.');
          this.applyWhatsappVerificationMeta(response.data || {});
          await this.refreshData();
          this.openWhatsappVerificationModal();
          return;
        }

        this.formError = response?.message || 'No fue posible enviar el código de confirmación.';
      } catch (error) {
        this.formError = error?.response?.data?.message || 'No fue posible enviar el código de confirmación.';
      }
    },
    openWhatsappVerificationModal() {
      this.whatsappVerificationDigits = ['', '', '', '', '', ''];
      this.whatsappVerificationError = '';
      this.showWhatsappVerificationModal = true;

      this.$nextTick(() => {
        this.focusWhatsappCodeInput(0);
      });
    },
    closeWhatsappVerificationModal() {
      this.showWhatsappVerificationModal = false;
      this.whatsappVerificationError = '';
      this.whatsappVerificationDigits = ['', '', '', '', '', ''];
      this.stopWhatsappResendCountdown();
    },
    onWhatsappCodeInput(index, event) {
      const value = (event?.target?.value || '').replace(/\D/g, '').slice(0, 1);
      this.whatsappVerificationDigits[index] = value;

      if (value && index < 5) {
        this.focusWhatsappCodeInput(index + 1);
      }

      this.triggerWhatsappCodeAutoConfirm();
    },
    onWhatsappCodeKeydown(index, event) {
      const key = event.key;

      if (event.ctrlKey || event.metaKey) {
        return;
      }

      if (key === 'Tab') {
        return;
      }

      if (/^\d$/.test(key)) {
        event.preventDefault();
        this.whatsappVerificationDigits[index] = key;

        if (index < 5) {
          this.focusWhatsappCodeInput(index + 1);
        }

        this.triggerWhatsappCodeAutoConfirm();
        return;
      }

      if (key === 'Backspace') {
        event.preventDefault();

        if (this.whatsappVerificationDigits[index]) {
          this.whatsappVerificationDigits[index] = '';
          return;
        }

        if (index > 0) {
          this.focusWhatsappCodeInput(index - 1);
          this.whatsappVerificationDigits[index - 1] = '';
        }
        return;
      }

      if (key === 'ArrowLeft') {
        event.preventDefault();
        if (index > 0) {
          this.focusWhatsappCodeInput(index - 1);
        }
        return;
      }

      if (key === 'ArrowRight') {
        event.preventDefault();
        if (index < 5) {
          this.focusWhatsappCodeInput(index + 1);
        }
        return;
      }

      if (key === 'Delete') {
        event.preventDefault();
        this.whatsappVerificationDigits[index] = '';
        return;
      }

      event.preventDefault();
    },
    handleWhatsappCodePaste(event) {
      event.preventDefault();
      const pasted = (event.clipboardData?.getData('text') || '').replace(/\D/g, '').slice(0, 6);

      if (!pasted) return;

      const digits = pasted.split('');
      for (let i = 0; i < 6; i += 1) {
        this.whatsappVerificationDigits[i] = digits[i] || '';
      }

      const focusIndex = Math.min(digits.length, 5);
      this.focusWhatsappCodeInput(focusIndex);

      this.triggerWhatsappCodeAutoConfirm();
    },
    getWhatsappCodeInputRef(index) {
      const refValue = this.$refs[`whatsappCodeDigit${index}`];
      if (Array.isArray(refValue)) {
        return refValue[0] || null;
      }

      return refValue || null;
    },
    focusWhatsappCodeInput(index) {
      this.$nextTick(() => {
        const input = this.getWhatsappCodeInputRef(index);
        if (!input || typeof input.focus !== 'function') {
          return;
        }

        input.focus();
        if (typeof input.select === 'function') {
          input.select();
        }
      });
    },
    triggerWhatsappCodeAutoConfirm() {
      if (!this.isWhatsappCodeComplete || this.isConfirmingWhatsappCode) {
        return;
      }

      this.confirmWhatsappVerification();
    },
    applyWhatsappVerificationMeta(meta) {
      const wait = Number(meta?.resend_available_in_seconds || 0);
      this.whatsappSupportWaLink = (meta?.support?.wa_link || '').toString().trim();
      this.startWhatsappResendCountdown(wait);
    },
    startWhatsappResendCountdown(seconds) {
      this.stopWhatsappResendCountdown();

      this.whatsappResendRemainingSeconds = Math.max(0, Number(seconds) || 0);
      if (this.whatsappResendRemainingSeconds <= 0) {
        return;
      }

      this.whatsappResendIntervalId = setInterval(() => {
        if (this.whatsappResendRemainingSeconds <= 1) {
          this.whatsappResendRemainingSeconds = 0;
          this.stopWhatsappResendCountdown();
          return;
        }

        this.whatsappResendRemainingSeconds -= 1;
      }, 1000);
    },
    stopWhatsappResendCountdown() {
      if (this.whatsappResendIntervalId) {
        clearInterval(this.whatsappResendIntervalId);
        this.whatsappResendIntervalId = null;
      }
    },
    async resendWhatsappVerificationCode() {
      if (!this.canResendWhatsappCode || this.isResendingWhatsappCode) {
        return;
      }

      this.whatsappVerificationError = '';
      this.isResendingWhatsappCode = true;

      try {
        const response = await accountService.requestWhatsappVerification({
          whatsapp_phone: this.account.whatsapp_phone,
        });

        if (response && response.type === 'success') {
          this.$toast.success(response.message || 'Se reenvio el codigo de confirmacion.');
          this.applyWhatsappVerificationMeta(response.data || {});
          return;
        }

        this.whatsappVerificationError = response?.message || 'No fue posible reenviar el código.';
      } catch (error) {
        this.whatsappVerificationError = error?.response?.data?.message || 'No fue posible reenviar el código.';
      } finally {
        this.isResendingWhatsappCode = false;
      }
    },
    async confirmWhatsappVerification() {
      if (!this.isWhatsappCodeComplete || this.isConfirmingWhatsappCode) {
        return;
      }

      this.isConfirmingWhatsappCode = true;
      this.whatsappVerificationError = '';

      try {
        const code = this.whatsappVerificationDigits.join('');
        const response = await accountService.confirmWhatsappVerification({
          whatsapp_phone: this.account.whatsapp_phone,
          code,
        });

        if (response && response.type === 'success') {
          this.$toast.success(response.message || 'Número de WhatsApp confirmado correctamente.');
          this.closeWhatsappVerificationModal();
          await this.refreshData();
          return;
        }

        this.whatsappVerificationError = response?.message || 'No fue posible confirmar el código.';
      } catch (error) {
        this.whatsappVerificationError = error?.response?.data?.message || 'No fue posible confirmar el código.';
      } finally {
        this.isConfirmingWhatsappCode = false;
      }
    },
  },
};
</script>
